import intl from 'react-intl-universal';
import { useState, useEffect, useRef, useCallback } from 'react';
import { BetaSchemaForm } from '@ant-design/pro-components';
import type {
    ProFormColumnsType,
    ProFormProps,
    ProFormInstance,
} from '@ant-design/pro-components';
import { Row, Col, message, Tag } from 'antd';
import { default as Button } from '@/components/CssBtn';

import moment from 'moment';
import type { ProvincesType, CitiesType, DistrictType } from '@/type/common';

import { getDictionaries, orderPreCheck } from '@/api/addr';
import { debounce, isNumber } from 'lodash';
import { commonRules } from '@/commonConfig';
import { TrademarkOutlined } from '@ant-design/icons';
import AddressParse from 'address-parse';
// import smart from 'address-smart-parse';
import { getTextByEncryp } from '@/api/common';
import sensorLoader from '@/utils/sensorLoader';

interface Props extends ProFormProps {
    type: number; // 1: 收件人； 2: 取件人
    id: string; // 主键id
    showGetOriginBtn?: boolean; // 是否展示获取原订单地址信息按钮
    address_info?: any; // 原订单地址信息
    aftersale_no: string;
    aftersaleType?: number; //工单类型
    readonly: boolean;
    value: any;
    onChange: (values: any) => void;
    // 获取原订单地址加密信息字段
    position?: number;
    sourceNo?: string;
    isShowDateTime?: boolean; // 是否展示取件时间
}
const detailAddressPlaceholder = intl.get(
    '示例: 王泡泡, 18866661117, 北京北京市朝阳区望京东园四区13号',
);
// i18n-ignore
const checkProvince = [
    '香港特别行政区',
    '澳门特别行政区',
    '台湾省',
    '香港',
    '澳门',
    '台湾',
];

const windowInnerWidth = 576;

const Addrs: React.FC<Props> = (props) => {
    const {
        type = 1,
        id,
        value,
        labelCol,
        onChange,
        address_info,
        aftersale_no,
        aftersaleType,
        readonly = false,
        showGetOriginBtn = TrademarkOutlined,
        position,
        sourceNo,
        size,
        isShowDateTime = true,
    } = props;
    // console.log('王泡泡, 18866661117', props);
    const currentValue = useRef(value);
    const addrForm = useRef<ProFormInstance>(null);

    // 省市区数据
    const [provinces, setProvinces] = useState<ProvincesType>([]);
    const [cities, setCities] = useState<CitiesType[]>([]);
    const [districts, setDistricts] = useState<DistrictType[]>([]);
    // 是否展示选择时间
    // const [showSelectDate, setShowSelectDate] = useState(type === 2);
    const [company, setCompany] = useState<string>();
    const [serviceDateList, setServiceDateList] = useState<any[]>([]);
    const [isShowIdentify, setIsShowIdentify] = useState(false);
    const cacheValue = useRef(value);

    useEffect(() => {
        getDictionaries().then((res) => {
            setProvinces(res?.data?.provinces || []);
        });
    }, []);

    useEffect(() => {
        // console.log('addr value change====', value);
    }, [value]);
    useEffect(() => {
        const {
            take_time_start,
            take_time_end,
            dateTime,
            province,
            city,
            district,
            detail_address,
        } = value || {};

        currentValue.current = value;

        if (take_time_end && take_time_start && !dateTime) {
            const date = moment(take_time_start).format('YYYY-MM-DD');
            const startHour = moment(take_time_start).hour();
            const endHour = moment(take_time_end).hour();
            onChange({
                ...value,
                dateTime: `${date} ${startHour}:00:00 - ${endHour}:00:00`,
            });
            return;
        }

        if (
            (province && province !== cacheValue.current?.province) ||
            (!cities?.length && !districts?.length)
        ) {
            handleProvinceChange(province, null, true);
        }

        if (type === 2 && isShowDateTime && !readonly) {
            // 上门取件，获取可预约时间
            // 售后弹窗表单不消失，每次刷新页面，数据可能都存在，因此需要对比是否数据和上次数据一致，不一致在获取上门取件时间
            const {
                province: cacheProvince,
                city: cacheCity,
                district: cacheDis,
                detail_address: cacheDetail,
            } = cacheValue.current || {};
            if (
                province !== cacheProvince ||
                city !== cacheCity ||
                district !== cacheDis ||
                detail_address !== cacheDetail
            ) {
                onPreCheck();
            }
        }

        cacheValue.current = value;
    }, [value, provinces]);

    useEffect(() => {
        if (readonly && addrForm.current) {
            addrForm.current?.setFieldsValue({
                [id]: value,
            });
        }
    }, [readonly, value]);

    const handleProvinceChange = (
        provinceName: string,
        a: any,
        isSaveCity: boolean = false,
    ) => {
        const currentProvince = provinces.find(
            (province) =>
                province.name === provinceName ||
                // 这里是容错，value是【北京市】 但是值是【北京】的情况
                province.name?.includes(provinceName) ||
                provinceName?.includes(province.name),
        );

        if (currentProvince) {
            const cities = currentProvince?.child_cities || [];
            const districts = cities[0]?.child_district || [];

            if (!isSaveCity) {
                // 如果是回显 比如获取原地址信息，则不 重置 市区的值
                onItemChange({
                    province: provinceName,
                    city: cities[0]?.name,
                    district: districts[0]?.name,
                });
            }

            setCities(cities);
            setDistricts(districts);
        }
    };

    const handleCityChange = (cityName: string) => {
        const currentCity = cities.find((city) => city.name === cityName);
        const districts = currentCity?.child_district || [];

        onItemChange({
            city: cityName,
            district: districts[0]?.name,
        });
        setDistricts(districts);
    };

    const onPreCheck = useCallback(
        debounce(function () {
            const {
                province = '',
                city = '',
                district = '',
                detail_address = '',
            } = currentValue.current;

            if (province && city && district && detail_address) {
                orderPreCheck({
                    address: detail_address,
                    aftersale_no,
                    aftersale_type: aftersaleType,
                    city,
                    county: district,
                    province,
                }).then((res) => {
                    if (res?.code === 0) {
                        if (res.data?.code === 2) {
                            message.error(
                                intl.get(
                                    '当前地址暂不支持上门取件，需选择用户自行寄回',
                                ),
                            );
                            // setShowSelectDate(false);
                            // 清空时间选择的数据
                            setServiceDateList([]);
                            onItemChange({
                                dateTime: undefined,
                                take_time_end: undefined,
                                take_time_start: undefined,
                            });
                            return;
                        }
                        setServiceDateList([]);
                        onItemChange({
                            dateTime: undefined,
                            take_time_end: undefined,
                            take_time_start: undefined,
                        });
                        setCompany(res?.data?.company);
                        setServiceDateList(res?.data?.serviceDateList || []);
                        // setShowSelectDate(true);
                    }
                });
            } else {
                // setShowSelectDate(false);
                setServiceDateList([]);
                // 清空时间选择的数据
                onItemChange({
                    dateTime: undefined,
                    take_time_end: undefined,
                    take_time_start: undefined,
                });
            }
        }, 1000),
        [addrForm],
    );

    const onItemChange = (itemValue: any) => {
        onChange({
            ...(currentValue.current || {}),
            ...itemValue,
        });
    };

    const detailAddressBlur = () => {
        if (!currentValue?.current?.identify_address) {
            message.info(intl.get('请填写详细地址'));
            return;
        }
        const {
            name = '',
            phone = '',
            identify_address = '',
        } = currentValue?.current;
        setIsShowIdentify(true);
        const handleIdentifyAddress = `${identify_address}`
            .replace(/&nbsp;/g, ',')
            .replace(/\s/g, '');
        const [result] = AddressParse.parse(handleIdentifyAddress);
        sensorLoader.reportClick('PopMartCustomerServiceIdentifyAddress', {
            address: currentValue?.current?.identify_address,
        });
        // console.log('result', result);
        const {
            name: newName = '',
            mobile: newMobile = '',
            province = '',
            city = '',
            area = '',
            details = '',
            __parse = 0, //数值越高 可信度越高，number，boolean
        } = result;
        if (!__parse || !city || !area || !details) {
            message.warning(intl.get('无法识别'));
            return;
        }
        if (province && checkProvince.includes(province)) {
            message.warning('售后服务地址超出合作快递配送范围');
            return;
        }

        const identifyInfo = {
            name: newName || name,
            phone: newMobile || phone,
            province,
            city,
            district: area,
            detail_address: details,
        };
        onItemChange(identifyInfo);
    };
    const labelColSize = () => {
        if (size === 'small' && window.innerWidth < windowInnerWidth) {
            return 0;
        } else {
            return labelCol?.span || 3;
        }
    };

    const getAddrColumns = (): ProFormColumnsType<any, 'addrSelectDate'>[] => {
        const namePreFix = type === 1 ? intl.get('收件') : intl.get('取件');
        const items: ProFormColumnsType<any, 'addrSelectDate'>[] = [
            {
                title: intl.get('{namePreFix}人', { namePreFix: namePreFix }),
                dataIndex: [id, 'name'],
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get('请输入{namePreFix}人', {
                                namePreFix: namePreFix,
                            }),
                        },
                    ],

                    labelCol: labelCol || { span: 3 },
                },
                fieldProps: {
                    maxLength: 20,
                    onChange: (e) => {
                        onItemChange({
                            name: e.target.value,
                        });
                    },
                },
            },
            {
                title: intl.get('联系电话'),
                dataIndex: [id, 'phone'],
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get(intl.get('请输入联系电话')),
                        },
                        ...commonRules.mobile,
                        // {
                        //     pattern: /^1[0-9]{10}$/,
                        //     message: '请输入正确的联系电话',
                        // },
                    ],
                    labelCol: labelCol || { span: 3 },
                },
                fieldProps: {
                    maxLength: commonRules.mobileLength,
                    onChange: (e) => {
                        onItemChange({
                            phone: e.target.value,
                        });
                    },
                },
            },
            {
                valueType: 'group',
                columns: [
                    {
                        title: intl.get('{namePreFix}地址', {
                            namePreFix: namePreFix,
                        }),
                        dataIndex: [id, 'province'],
                        valueType: 'select',
                        className: 'group-item',
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],

                            labelCol: labelCol || { span: 3 },
                        },
                        fieldProps: {
                            placeholder: intl.get('请选择省'),
                            options: provinces.map((province) => ({
                                label: province.name,
                                value: province.name,
                            })),
                            onChange: handleProvinceChange,
                        },
                    },
                    {
                        dataIndex: [id, 'city'],
                        valueType: 'select',
                        colProps: {
                            span: isNumber(labelColSize())
                                ? 24 - Number(labelColSize())
                                : 21,
                            offset: labelColSize(),
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],
                        },
                        fieldProps: {
                            placeholder: intl.get('请选择市'),
                            options: cities.map((city) => ({
                                label: city.name,
                                value: city.name,
                            })),
                            onChange: handleCityChange,
                        },
                    },
                    {
                        dataIndex: [id, 'district'],
                        valueType: 'select',
                        colProps: {
                            span: isNumber(labelColSize())
                                ? 24 - Number(labelColSize())
                                : 21,
                            offset: labelColSize(),
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],
                        },
                        fieldProps: {
                            placeholder: intl.get('请选择县 / 区'),
                            options: districts.map((district) => ({
                                label: district.name,
                                value: district.name,
                            })),
                        },
                    },
                    {
                        dataIndex: [id, 'detail_address'],
                        valueType: 'textarea',
                        colProps: {
                            span: isNumber(labelColSize())
                                ? 24 - Number(labelColSize())
                                : 21,
                            offset: labelColSize(),
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        intl.get('请输入详细地址'),
                                    ),
                                },
                            ],
                        },
                        render: (text) => text,
                        fieldProps: {
                            placeholder: intl.get('请输入详细地址'),
                            maxLength: 100,
                            // onBlur: detailAddressBlur,
                        },
                    },
                ],
            },
        ];

        if (!readonly) {
            const identifyItem: ProFormColumnsType<any>[] = [
                {
                    title: intl.get('智能识别'),
                    dataIndex: [id, 'identify_address'],
                    valueType: 'textarea',
                    formItemProps: {
                        labelCol: labelCol || { span: 3 },
                    },
                    render: (text) => text,
                    fieldProps: {
                        placeholder: detailAddressPlaceholder,
                        maxLength: 100,
                    },
                },
                {
                    dataIndex: 'identify',
                    readonly: true,
                    colProps: {
                        offset: labelColSize(),
                    },
                    render: () => {
                        return (
                            <Button
                                isNeedLoad={false}
                                type="primary"
                                size="small"
                                style={{ padding: '2px 6px' }}
                                onClick={detailAddressBlur}
                            >
                                {intl.get('智能识别')}
                            </Button>
                        );
                    },
                },
            ];

            items.unshift(...identifyItem);
        }

        if (type === 2 && isShowDateTime) {
            const timeItems: ProFormColumnsType<any, 'addrSelectDate'>[] = [
                // !readonly
                //     ? {
                //           title: '物流公司',
                //           dataIndex: 'company',
                //           valueType: 'select',
                //           formItemProps: {
                //               labelCol: labelCol || { span: 3 },
                //           },
                //           fieldProps: {
                //               disabled: true,
                //               value: company,
                //           },
                //           request: async () => {
                //               try {
                //                   const res = await getExpress();
                //                   return res.data || [];
                //               } catch (e) {
                //                   console.error(e);
                //                   return [];
                //               }
                //           },
                //       }
                //     : {
                //           title: '物流公司',
                //           dataIndex: [id, 'logistics_company'],
                //           formItemProps: {
                //               labelCol: labelCol || { span: 3 },
                //           },
                //       },
                {
                    title: intl.get('{namePreFix}时间', {
                        namePreFix: namePreFix,
                    }),
                    dataIndex: [id, 'dateTime'],
                    valueType: 'addrSelectDate',
                    formItemProps: {
                        labelCol: labelCol || { span: 3 },
                        rules: [
                            {
                                required: true,
                                message: intl.get(intl.get('请选择取件时间')),
                            },
                        ],
                    },
                    fieldProps: {
                        serviceDateList,
                        company,
                        onSelect: (dateTime: string, item: any) => {
                            onItemChange({
                                dateTime,
                                take_time_end: `${item?.date} ${item?.endTime}:00:00`,
                                take_time_start: `${item?.date} ${item?.startTime}:00:00`,
                            });
                        },
                    },
                },
                {
                    dataIndex: [id, 'take_time_end'],
                    formItemProps: {
                        hidden: true,
                    },
                },
                {
                    dataIndex: [id, 'take_time_start'],
                    formItemProps: {
                        hidden: true,
                    },
                },
            ];

            items.push(...timeItems);
        }

        return items;
    };

    const getOrderDetail = () => {
        if (address_info) {
            console.log(address_info, aftersale_no, aftersaleType);

            getTextByEncryp({
                type: position || 0,
                sourceNo,
                encryptList: [
                    address_info.phoneEncrypt,
                    address_info.addressEncrypt,
                ],
            }).then((res) => {
                if (res?.data?.length) {
                    onItemChange({
                        ...(address_info || {}),
                        phone: res?.data?.[0] || address_info.phone,
                        detail_address: res?.data?.[1] || address_info.address,
                    });
                } else {
                    message.warning(intl.get('无法获取解密后的信息'));
                    onItemChange({
                        ...(address_info || {}),
                        detail_address: address_info.address,
                    });
                }
            });

            // onItemChange({
            //     ...(address_info || {}),
            //     detail_address: address_info.address,
            // });
        } else {
            message.warning(intl.get('原订单无地址信息'));
        }
    };

    return (
        <div id={id}>
            {!readonly && showGetOriginBtn && (
                <Row>
                    <Col
                        offset={
                            size === 'small' &&
                            window.innerWidth < windowInnerWidth
                                ? 0
                                : labelCol?.span || 3
                        }
                    >
                        <Button
                            type="link"
                            size="small"
                            onClick={getOrderDetail}
                        >
                            {intl.get('原订单信息')}
                        </Button>
                    </Col>
                </Row>
            )}
            {!readonly && isShowIdentify && (
                <Row style={{ margin: '4px 0' }}>
                    <Col
                        offset={
                            size === 'small' &&
                            window.innerWidth < windowInnerWidth
                                ? 0
                                : labelCol?.span || 3
                        }
                    >
                        <Tag color="orange">
                            {intl.get(
                                '地址已智能识别，请检查是否准确，如有错漏请及时修正',
                            )}
                        </Tag>
                    </Col>
                </Row>
            )}
            {readonly && (
                <div>
                    <BetaSchemaForm<any, 'addrSelectDate'>
                        formRef={addrForm}
                        key={type}
                        readonly={readonly}
                        layout="horizontal"
                        colon={true}
                        grid={true}
                        submitter={{
                            render: () => [],
                        }}
                        columns={getAddrColumns()}
                    ></BetaSchemaForm>
                </div>
            )}

            {!readonly && (
                <BetaSchemaForm<any, 'addrSelectDate'>
                    key={type}
                    readonly={readonly}
                    layout="horizontal"
                    layoutType="Embed"
                    columns={getAddrColumns()}
                ></BetaSchemaForm>
            )}
        </div>
    );
};

export default Addrs;
